import React, { useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';

import {
  SubmitRetailRateRequestRequest,
  RetailRateRequestProofType,
} from '@classpass/protobuf-definitions/lib/web/models/partners/retail_rate_requests';

import { createInfoBanner, createErrorBanner } from 'components/Banner';
import { Card, CardSection } from 'components/Card';
import { openDecagonChat, prepopulateDecagonMessage } from 'components/Decagon';
import { DropdownToggle, DropdownMenu, DropdownItem } from 'components/Dropdown2';
import { CharactersRemaining } from 'components/Form';
import { Form, Input, Dropdown, Textarea } from 'components/HookForm';
import Message from 'components/Message';
import { closeModal, openModal } from 'components/Modal';

import {
  NEW_SERVICE_BANNER_LOCAL_STORAGE_KEY,
  SMALL_TEXT_MAX_CHARACTERS,
  MEDIUM_TEXT_MAX_CHARACTERS,
  MANAGE_SERVICES_TAB,
} from 'constants/logic';
import { intlMessage, manageServicesAvailabilityShape } from 'constants/propTypes';
import URLS from 'constants/urls';

import Title from 'features/App/Title';
import { goToItem } from 'features/ManageServices/ManageServices.saga';

import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { bem } from 'helpers/class-name';
import { toMajorUnits, toMinorUnits } from 'helpers/currency-symbol';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import { makePath } from 'helpers/link-generation';
import { setKeyForUser } from 'helpers/local-storage';
import Segment from 'helpers/segment';

import { useIsServicePricingProofEnabled } from 'hooks/useIsServicePricingProofEnabled';

import { selectRouteParams, selectUserId, selectActiveVenue, selectUserInfo } from 'selectors/general';
import { selectLocaleData } from 'selectors/locale-data';

import { handler, transformData } from 'store/saga/entities/services';

import ServicePricingSection, { PRICING_MODE } from '../../Components/ServicePricingSection';
import { isManualHandoffAlreadyPriced } from '../../Components/ServicePricingSection/pricingStatusStepper.helpers';
import { parsePriceToMinorUnits } from '../../Components/ServicePricingSection/servicePricingSection.helpers';
import { messages as pricingMessages } from '../../Components/ServicePricingSection/ServicePricingSection.messages';
import { blocksEditing, isRejected } from '../../Components/PricingStatusStepper';
import FooterForm from '../../FooterForm/FooterFormContainer';
import { messages } from '../Services.messages';
import { fetchServices, fetchServicesPractitionersResources } from '../Services.saga';
import ServicesContext from '../ServicesContext/ServicesContext';
import BookablePicker from './BookablePicker/BookablePicker';
import LegacyServicePricingInputs from './LegacyServicePricingInputs';
import {
  getNotListedOnline,
  openPriceSubmitErrorModal as dispatchPriceSubmitErrorModal,
} from './PriceSubmitErrorModal/priceSubmitErrorModal.helpers';
import { resolveCalculatedPayout } from './serviceForm.helpers';

import './ServiceForm.scss';

export const MAX_DURATION = 240;
export const INTERVAL_IN_MINUTES = 5;

const getDurations = intl => {
  const durations = [];

  for (let i = 1; i <= MAX_DURATION / INTERVAL_IN_MINUTES; i += 1) {
    const duration = i * INTERVAL_IN_MINUTES;
    const label = intl.formatMessage(messages.form.duration, { duration });
    durations.push({
      label,
      value: duration.toString(),
      match: label,
    });
  }

  return durations;
};

const { block, element } = bem('service-form');

function ServiceForm(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { selectedService, isLoading: contextIsLoading, refreshServices } = useContext(ServicesContext);
  const selectedServiceId = selectedService?.id;

  const { isEditing, handleDelete, handleCancel, showDeleteButton, title, practitioners, resources, venueId } = props;

  // Use context data for editing, defaults for add mode
  const isLoading = isEditing ? contextIsLoading : false;
  const serviceName = isEditing ? selectedService?.name : undefined;
  const defaultValues = isEditing
    ? {
        name: selectedService?.name,
        description: selectedService?.description,
        duration: selectedService?.duration?.toString(),
        practitionerIds: selectedService?.practitioners?.map(({ id }) => id),
        resourceIds: selectedService?.resources?.map(({ id }) => id),
        // Carry the prior accountability attestation forward with the price.
        servicePrice: selectedService?.price,
        accountabilityAgreement: !_isNil(selectedService?.price),
      }
    : undefined;

  const intl = useIntl();
  const durationOptions = getDurations(intl);

  const venue = useSelector(selectActiveVenue);
  const userId = useSelector(selectUserId);
  const { adminUserId } = useSelector(selectUserInfo);
  const { locale } = useSelector(selectLocaleData);
  const isAdminUser = !!adminUserId;
  const pricingRef = useRef(null);
  const { itemId: itemIdFromParams } = useSelector(selectRouteParams);

  const dirtyFieldsRef = useRef({});

  // `null` while resolving; only `false` once it settles off, so the safe
  // default keeps the new section (and pricingRef) wired.
  const isPricingProofEnabled = useIsServicePricingProofEnabled();

  // Legacy pricing applies only to the manual-studio add (input) flow with the
  // flag off. Integrated studios and the readonly edit payout always go through
  // ServicePricingSection and are unaffected by the flag.
  const useLegacyPricing = !isEditing && !!venue.isManualStudio && isPricingProofEnabled === false;

  // Read the request ServicesContext resolved by class_id — same source as the
  // list badge, so the edit page can't disagree with the list.
  const editedService = isEditing ? selectedService : null;
  const hasExistingPrice = !_isNil(editedService?.price);
  const pricingRequest =
    isPricingProofEnabled === true && !isManualHandoffAlreadyPriced(editedService?.retailRateRequest, hasExistingPrice)
      ? editedService?.retailRateRequest ?? null
      : null;
  // A pending/approved request shows the timeline read-only; rejected
  // (action-required) and terminal/unknown statuses stay editable so the
  // partner can always resubmit.
  const requestBlocksEditing = blocksEditing(pricingRequest);

  // Editable only with the flag on, and only when there is something for the
  // partner to do: no price yet, or a rejected request to fix. An already
  // priced service with no action needed keeps the read-only payout view.
  const editablePricingOnEdit =
    isEditing &&
    isPricingProofEnabled === true &&
    !requestBlocksEditing &&
    (!hasExistingPrice || isRejected(pricingRequest));
  const pricingMode = isEditing && !editablePricingOnEdit ? PRICING_MODE.READONLY : PRICING_MODE.INPUT;

  const handleClickCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return <Card loading />;
  }

  async function openNoEquipmentOrPractitionerModal() {
    return new Promise((resolve, reject) => {
      dispatch(
        openModal('NoEquipmentOrPractitionerModal', {
          onConfirm: () => resolve(true),
          onRequestClose: reject,
        })
      );
    }).catch(() => false);
  }

  function openPostSubmissionModal(payload, payoutConfirmationMessage) {
    dispatch(
      openModal('ServiceInReviewModal', {
        serviceName: payload.name,
        duration: payload.duration,
        ...(payoutConfirmationMessage ? { payoutConfirmationMessage } : {}),
      })
    );
  }

  function openPriceSubmitErrorModal(payload, notListedOnline) {
    dispatchPriceSubmitErrorModal(dispatch, {
      currencyCode: venue.currencyCode,
      serviceName: payload.name,
      linkToPrice: payload.linkToPrice,
      servicePrice: payload.servicePrice,
      notListedOnline,
    });
  }

  // LEGACY (flag: partner_dashboard_service_pricing_proof OFF) — the pre-proof-flow
  // pricing submit: URL-only proof, accountability hardcoded false,
  // not_listed_online sentinel. Remove with the flag and LegacyServicePricingInputs.
  async function createServicePricing(msiServiceUuid, payload) {
    const proofUrl = payload.notListedOnline ? 'not_listed_online' : payload.linkToPrice;

    const request = {
      ...SubmitRetailRateRequestRequest.toJSON({
        venue_id: venueId,
        retail_rate: {
          currency_code: venue.currencyCode,
          currency_units: toMinorUnits(venue.currencyCode, payload.servicePrice),
        },
        proof: [
          {
            type: RetailRateRequestProofType.RETAIL_RATE_REQUEST_PROOF_TYPE_URL,
            url: proofUrl,
            uploaded_by: String(userId),
          },
        ],
        accountability_agreement: false,
      }),
      msi_service_uuid: msiServiceUuid,
    };

    return httpClient.post(URLS.manageServices.services.submitRetailRateRequest(), request, {
      ignoreForbiddenError: true,
    });
  }

  async function createService(payload, formAsyncFns) {
    let successfullySubmittedPricing = null;
    let payoutDialogOpened = false;
    const { resolveSubmit, rejectSubmit } = formAsyncFns;

    try {
      if (_isEmpty(payload.practitionerIds) && _isEmpty(payload.resourceIds)) {
        const confirmed = await openNoEquipmentOrPractitionerModal();
        dispatch(closeModal());

        if (!confirmed) {
          return rejectSubmit();
        }
      }

      // Collect proof file references BEFORE creating the service so a failed
      // upload aborts the whole flow and we don't end up with an orphaned
      // service with no pricing request. (New pricing-proof flow only — the
      // legacy path had no proof upload and no pre-create guard.)
      let fileProofs = [];
      if (!isAdminUser && !useLegacyPricing && pricingRef.current) {
        try {
          fileProofs = await pricingRef.current.collectProofs();
        } catch (error) {
          reportError('Proof file upload failed before service creation', { error });
          dispatch(createErrorBanner(messages.error.problemWithRequest));
          return rejectSubmit();
        }

        // Proof is mandatory for manual studios: a pricing link OR a file.
        // Abort before creating the service so the partner can fix it without
        // an orphaned, proof-less service.
        const proofCheck = pricingRef.current.validateProof({ formValues: payload, fileProofs });
        if (!proofCheck.valid) {
          dispatch(createErrorBanner(proofCheck.error));
          return rejectSubmit();
        }
      }

      const createEndpoint = URLS.manageServices.services.create(venueId);
      let itemId;
      let msiServiceUuid;

      if (isAdminUser || useLegacyPricing) {
        const createResponse = await httpClient.post(createEndpoint, transformData(payload));
        const createdService = createResponse.data;
        itemId = createdService.id;
        msiServiceUuid = createdService.uuid;

        if (!isAdminUser) {
          try {
            await createServicePricing(msiServiceUuid, payload);
            successfullySubmittedPricing = true;
          } catch (error) {
            reportError('Error submitting pricing request', { error });
            successfullySubmittedPricing = false;
          }
        }
      } else {
        const priceInMinorUnits = parsePriceToMinorUnits(venue.currencyCode, payload.servicePrice, locale);
        let payoutCheckFailed = false;
        let payoutCurrencyCode;
        let payoutCalculated;
        let payoutPercentage;
        let payoutPercentageSource;
        let payoutAvailable;

        try {
          const payoutResponse = await httpClient.post(
            URLS.manageServices.services.calculatedPayout(),
            {
              venue_id: venue.venueId,
              price: { currency_code: venue.currencyCode, currency_units: priceInMinorUnits },
            },
            { ignoreForbiddenError: true }
          );
          ({ payout_available: payoutAvailable } = payoutResponse.data);
          ({
            payoutCurrencyCode,
            payoutCurrencyUnits: payoutCalculated,
            payoutPercentage,
            payoutPercentageSource,
          } = resolveCalculatedPayout(payoutResponse.data));
        } catch (error) {
          reportError('Error fetching calculated payout', { error });
          payoutCheckFailed = true;
        }

        if (payoutAvailable) {
          let dialogResolved = false;
          payoutDialogOpened = true;
          dispatch(
            openModal('PayoutAgreementDialog', {
              payoutAmount: toMajorUnits(payoutCurrencyCode, payoutCalculated),
              currencyCode: payoutCurrencyCode,
              onAccept: async () => {
                dialogResolved = true;
                try {
                  const createResponse = await httpClient.post(createEndpoint, transformData(payload));
                  const createdService = createResponse.data;
                  await pricingRef.current.submitPricing({
                    msiServiceUuid: createdService.uuid,
                    formValues: payload,
                    fileProofs,
                    payoutAgreementStatus: 'PAYOUT_AGREEMENT_STATUS_ACCEPTED',
                    payoutCurrencyCode,
                    payoutCurrencyUnits: payoutCalculated,
                    payoutPercentage,
                    payoutPercentageSource,
                  });
                  setKeyForUser(userId, NEW_SERVICE_BANNER_LOCAL_STORAGE_KEY, true);
                  dispatch(fetchServices());
                  braze.trackEvent(BRAZE_CUSTOM_EVENTS.NEW_SERVICE_ADDED);
                  Segment.event('Service Created', {
                    'service id': createdService.id,
                    'venue id': venueId,
                    'service name': payload.name,
                    'service duration': payload.duration,
                  });
                  resolveSubmit();
                  dispatch(goToItem(createdService.id));
                  openPostSubmissionModal(payload);
                } catch (error) {
                  rejectSubmit();
                  reportError('Error creating service or submitting pricing after payout accept', { error });
                  openPriceSubmitErrorModal(payload, false);
                }
              },
              onDecline: async () => {
                dialogResolved = true;
                try {
                  const createResponse = await httpClient.post(createEndpoint, transformData(payload));
                  const createdService = createResponse.data;
                  await pricingRef.current.submitPricing({
                    msiServiceUuid: createdService.uuid,
                    formValues: payload,
                    fileProofs,
                    payoutAgreementStatus: 'PAYOUT_AGREEMENT_STATUS_DECLINED',
                    payoutCurrencyCode,
                    payoutCurrencyUnits: payoutCalculated,
                    payoutPercentage,
                    payoutPercentageSource,
                  });
                  setKeyForUser(userId, NEW_SERVICE_BANNER_LOCAL_STORAGE_KEY, true);
                  dispatch(fetchServices());
                  braze.trackEvent(BRAZE_CUSTOM_EVENTS.NEW_SERVICE_ADDED);
                  Segment.event('Service Created', {
                    'service id': createdService.id,
                    'venue id': venueId,
                    'service name': payload.name,
                    'service duration': payload.duration,
                  });
                  resolveSubmit();
                  dispatch(goToItem(createdService.id));
                  openPostSubmissionModal(payload, pricingMessages.payoutDeclineConfirmation);
                } catch (error) {
                  rejectSubmit();
                  reportError('Error creating service or submitting pricing after payout decline', { error });
                  openPriceSubmitErrorModal(payload, false);
                }
              },
              onXClose: rejectSubmit,
              onClose: () => {
                if (!dialogResolved) rejectSubmit();
              },
            })
          );
        } else {
          const createResponse = await httpClient.post(createEndpoint, transformData(payload));
          const createdService = createResponse.data;
          itemId = createdService.id;
          msiServiceUuid = createdService.uuid;
          try {
            await pricingRef.current.submitPricing({
              msiServiceUuid,
              formValues: payload,
              fileProofs,
              payoutAgreementStatus: 'PAYOUT_AGREEMENT_STATUS_UNAVAILABLE',
              payoutCurrencyCode: payoutCheckFailed ? undefined : payoutCurrencyCode,
              payoutCurrencyUnits: payoutCheckFailed ? null : payoutCalculated,
              payoutPercentage: payoutCheckFailed ? undefined : payoutPercentage,
              payoutPercentageSource: payoutCheckFailed ? null : payoutPercentageSource,
            });
            successfullySubmittedPricing = true;
          } catch (error) {
            reportError(
              payoutCheckFailed
                ? 'Error submitting pricing after payout API failure'
                : 'Error submitting pricing after unavailable payout',
              { error }
            );
          }
        }
      }

      if (!payoutDialogOpened) {
        setKeyForUser(userId, NEW_SERVICE_BANNER_LOCAL_STORAGE_KEY, true);
        dispatch(fetchServices());
        resolveSubmit();
        dispatch(goToItem(itemId));

        if (!isAdminUser) {
          if (successfullySubmittedPricing) {
            openPostSubmissionModal(payload);
          } else {
            const notListedOnline = getNotListedOnline(payload, {
              useLegacyCheckbox: useLegacyPricing,
              hasFileProof: fileProofs.length > 0,
            });
            openPriceSubmitErrorModal(payload, notListedOnline);
          }
        }

        braze.trackEvent(BRAZE_CUSTOM_EVENTS.NEW_SERVICE_ADDED);
        Segment.event('Service Created', {
          'service id': itemId,
          'venue id': venueId,
          'service name': payload.name,
          'service duration': payload.duration,
        });
      }
    } catch (error) {
      reportError('Error creating service', { error });
      dispatch(createErrorBanner(messages.error.problemWithRequest));
      rejectSubmit();
    }
  }

  async function updateService(payload, formAsyncFns) {
    const { resolveSubmit, rejectSubmit } = formAsyncFns;

    // Only resubmit when a pricing field is dirty: prefill alone shouldn't fire
    // a retail-rate-request, which on a name-only edit could also fail the save
    // when the prior proof can't be re-derived (e.g. a legacy file proof with
    // no id).
    const resubmitPricing =
      editablePricingOnEdit &&
      !isAdminUser &&
      !!pricingRef.current &&
      (dirtyFieldsRef.current.servicePrice ||
        dirtyFieldsRef.current.linkToPrice ||
        dirtyFieldsRef.current.accountabilityAgreement ||
        dirtyFieldsRef.current.proofFiles);

    // Collect + validate proof BEFORE persisting the service (mirrors
    // createService): a proof problem shows a specific error and aborts the
    // save, so we never leave the service fields updated without a matching
    // pricing request.
    let fileProofs = [];
    if (resubmitPricing) {
      try {
        fileProofs = await pricingRef.current.collectProofs();
      } catch (error) {
        reportError('Proof file upload failed before service update', { error });
        dispatch(createErrorBanner(messages.error.problemWithRequest));
        return rejectSubmit();
      }

      const proofCheck = pricingRef.current.validateProof({ formValues: payload, fileProofs });
      if (!proofCheck.valid) {
        dispatch(createErrorBanner(proofCheck.error));
        return rejectSubmit();
      }
    }

    try {
      await handler({ httpClient }, venueId, payload);

      if (resubmitPricing) {
        try {
          await pricingRef.current.submitPricing({
            msiServiceUuid: selectedService?.uuid,
            formValues: payload,
            fileProofs,
          });
        } catch (error) {
          reportError('Error submitting pricing request', { error });
          dispatchPriceSubmitErrorModal(dispatch, {
            currencyCode: venue.currencyCode,
            serviceName: payload.name,
            linkToPrice: payload.linkToPrice,
            servicePrice: payload.servicePrice,
            notListedOnline: getNotListedOnline(payload, { hasFileProof: fileProofs.length > 0 }),
          });
        }
      }

      dispatch(createInfoBanner(messages.success.serviceUpdated));

      dispatch(fetchServicesPractitionersResources());
      resolveSubmit();
      dispatch(goToItem(itemIdFromParams));
    } catch (error) {
      reportError('Error updating service', { error });
      dispatch(createErrorBanner(messages.error.problemWithRequest));
      rejectSubmit();
    }
  }

  async function createOrUpdateService(payload, formAsyncFns) {
    if (isEditing) {
      await updateService({ ...payload, id: selectedServiceId }, formAsyncFns);
    } else {
      await createService(payload, formAsyncFns);
    }

    await refreshServices();
  }

  function openDecagonChatAndPrepopulateMessage() {
    openDecagonChat();
    prepopulateDecagonMessage(
      intl.formatMessage(messages.form.pricingQuestion, {
        serviceName: selectedService?.name,
        serviceId: selectedService?.id,
        venueId,
      })
    );
  }

  return (
    <Form
      id="serviceForm"
      defaultValues={defaultValues}
      onSubmit={createOrUpdateService}
      onReset={handleCancel}
      isLoading={isLoading}
      className={block()}>
      {({ formState, watch }) => {
        const { isDirty, isSubmitting, dirtyFields } = formState;
        const descriptionValue = watch('description');
        // Snapshot for the async submit handler, which can't read formState.
        dirtyFieldsRef.current = dirtyFields;

        return (
          <>
            <Card flush className={element('card')}>
              <Message as={Title} message={title} values={{ serviceName }} />

              <CardSection>
                <Input
                  name="name"
                  type="text"
                  label={messages.form.name}
                  placeholder={messages.form.namePlaceholder}
                  maxLength={SMALL_TEXT_MAX_CHARACTERS}
                  required
                />
                <Textarea
                  name="description"
                  label={messages.form.description}
                  placeholder={messages.form.descriptionPlaceholder}
                  maxLength={MEDIUM_TEXT_MAX_CHARACTERS}
                  className="mb-"
                  optional
                />
                <CharactersRemaining length={MEDIUM_TEXT_MAX_CHARACTERS} value={descriptionValue || ''} />

                <Dropdown
                  name="duration"
                  label={messages.form.durationLabel}
                  autocomplete
                  options={durationOptions}
                  required
                  className="mv+"
                  dataCypress="duration-picker">
                  {({ hasError, value, onChange }) => (
                    <>
                      <DropdownToggle placeholder={messages.form.selectDuration} hasError={hasError}>
                        {value}
                      </DropdownToggle>
                      <DropdownMenu>
                        {data =>
                          data.map(({ option, isHighlighted, ref }) => {
                            return (
                              <DropdownItem
                                key={option.match}
                                ref={ref}
                                isSelected={option.value === value}
                                isHighlighted={isHighlighted}
                                onClick={() => onChange(option.value)}>
                                {option.label}
                              </DropdownItem>
                            );
                          })
                        }
                      </DropdownMenu>
                    </>
                  )}
                </Dropdown>

                {useLegacyPricing ? (
                  <LegacyServicePricingInputs />
                ) : (
                  <ServicePricingSection
                    ref={pricingRef}
                    mode={pricingMode}
                    currentPrice={editedService?.price}
                    prefillEnabled
                    onContactSupport={openDecagonChatAndPrepopulateMessage}
                    isAlreadyBookable={hasExistingPrice}
                    retailRateRequest={pricingRequest}
                  />
                )}

                <hr className="mv+" />

                <BookablePicker
                  name="practitionerIds"
                  titleMessage={messages.form.practitionersTitle}
                  subtitleMessage={messages.form.practitionersSubtitle}
                  addElementMessage={messages.form.addPractitionersLink}
                  addElementLink={makePath.manageServicesAdd({ venueId, tab: MANAGE_SERVICES_TAB.PRACTITIONERS })}
                  editItemLink={itemId =>
                    makePath.manageServicesEdit({ venueId, tab: MANAGE_SERVICES_TAB.PRACTITIONERS, itemId })
                  }
                  bookables={practitioners}
                />

                <hr className="mv+" />

                <BookablePicker
                  name="resourceIds"
                  titleMessage={messages.form.equipmentTitle}
                  subtitleMessage={messages.form.equipmentSubtitle}
                  addElementMessage={messages.form.addEquipmentLink}
                  addElementLink={makePath.manageServicesAdd({ venueId, tab: MANAGE_SERVICES_TAB.RESOURCES })}
                  editItemLink={itemId =>
                    makePath.manageServicesEdit({ venueId, tab: MANAGE_SERVICES_TAB.RESOURCES, itemId })
                  }
                  bookables={resources}
                />
              </CardSection>

              <FooterForm
                deleteMessage={messages.details.deleteService}
                submitMessage={messages.details.submitForReview}
                formName="serviceForm"
                visible
                submitDisabled={!isDirty}
                submitting={isSubmitting}
                showDelete={showDeleteButton}
                onClickCancel={handleClickCancel}
                onClickDelete={handleDelete}
              />
            </Card>
          </>
        );
      }}
    </Form>
  );
}

ServiceForm.propTypes = {
  isEditing: PropTypes.bool,
  handleDelete: PropTypes.func,
  handleCancel: PropTypes.func,
  showDeleteButton: PropTypes.bool,
  title: intlMessage,
  practitioners: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      recurrence: manageServicesAvailabilityShape.recurrence,
    })
  ),
  resources: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      recurrence: manageServicesAvailabilityShape.recurrence,
    })
  ),
  venueId: PropTypes.number,
};

export default ServiceForm;
